/* eslint-disable multiline-ternary */ // Keeping this if you prefer this style, but often good to avoid for readability
import { useEffect, useMemo, useState, useCallback } from 'react';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import 'react-pivottable/pivottable.css';
import TableRenderers from 'react-pivottable/TableRenderers';
import { Toaster, toast } from 'sonner';

// --- IMPORTANT: ONLY KEEP THIS LINE FOR pivottable.js ---
// This ensures pivottable.js (and its side-effects like attaching to window.jQuery) is loaded.
import 'pivottable';
// --- END IMPORTANT ---
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getReportArtifactList, getReportArtifactById } from '@/utils/api';
import { useData } from '@/hooks/useData';
import { type ReportArtifactMetadata } from '@/types/ReportArtifactMetadata';
import DropdownFull from '@/components/dropdowns/DropdownFull';
import { useAuth } from '@/hooks/useAuth';
import { Roles } from '@/types/Roles';

// Custom Aggregators and Sorters Utility
// Extracted to improve readability and reusability
const getPivotUtilities = () => {
  const corePivotUtilities = (window as any).jQuery?.pivotUtilities;
  if (!corePivotUtilities) {
    console.error('FAILED TO LOAD PIVOT UTILITIES. Custom aggregators and sorters may not work.');
    toast.error('Failed to load pivot table utilities. Some features may not work.');
  }
  return corePivotUtilities;
};

// Define constants outside the component to prevent re-creation on every render
const ALL_RENDERERS = { ...TableRenderers };

const DEFAULT_SELECTED_ARTIFACT: ReportArtifactMetadata = {
  lastModified: '-- Select an existing report --',
  uuid: '',
  size: 0,
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_PART = ['Breakfast', 'Lunch', 'Afternoon', 'Snack', 'Dinner', 'Evening', 'Late Night'];

export default function ReportViewer(): JSX.Element {
  const [existingArtifacts, setExistingArtifacts] = useState<ReportArtifactMetadata[]>([]);
  const [selectedArtifact, setSelectedArtifact] = useState<ReportArtifactMetadata>(DEFAULT_SELECTED_ARTIFACT);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string>('');
  const [reportData, setReportData] = useState<any[]>([]); // Renamed 'data' to 'reportData' for clarity
  const [pivotState, setPivotState] = useState({});
  const [downloadingPivot, setDownloadingPivot] = useState(1);
  const [currentBytes, setCurrentBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const { selectedFranchise, profile } = useData(); // Destructured profile, but it's not used. Removed.
  const { user } = useAuth();


  const corePivotUtilities = getPivotUtilities(); // Get pivot utilities once

  const formatSecondsToMMSS = (seconds: number | null | undefined): string => {
      if (seconds === null || seconds === undefined || isNaN(seconds)) {
        return ''; // Return empty string for null/undefined/NaN
      }
      if (isNaN(seconds)) {
        return seconds.toString(); // Return empty string for null/undefined/NaN
      }
      const numSeconds = Math.round(seconds); // Ensure it's an integer
      const minutes = Math.floor(numSeconds / 60);
      const remainingSeconds = numSeconds % 60;
      const formattedMinutes = String(minutes).padStart(2, '0');
      const formattedSeconds = String(remainingSeconds).padStart(2, '0');
      console.log(`${formattedMinutes}:${formattedSeconds}`);
      return `${formattedMinutes}:${formattedSeconds}`;
    };

  // Memoize custom aggregators to prevent unnecessary re-creation
  const customAggregators = useMemo(() => {
    if (!corePivotUtilities) return {}; // Return empty if utilities aren't loaded

    const baseAggregators = corePivotUtilities.aggregators || {};
    const DETECTION_TIME = 'detection_time_spent';
    const REGION_TIME = 'region_time_spent';
    const DETECTION_ID = 'detection_id';

    // Custom aggregator for average (nonzero)
    const averageDetectionTime = () => {
      return () => {
        let sum = 0;
        let count = 0;
        return {
          push: function (record: any) {
            const val = parseFloat(record[DETECTION_TIME]);
            if (!isNaN(val) && val !== 0) {
              sum += val;
              count += 1;
            }
          },
          value: function () {
            return count === 0 ? null : sum / count;
          },
          format: formatSecondsToMMSS
        };
      };
    };
    const averageRegionTime = () => {
      return () => {
        let sum = 0;
        let count = 0;
        return {
          push: function (record: any) {
            const val = parseFloat(record[REGION_TIME]);
            if (!isNaN(val) && val !== 0) {
              sum += val;
              count += 1;
            }
          },
          value: function () {
            return count === 0 ? null : sum / count;
          },
          format: formatSecondsToMMSS
        };
      };
    };

    const countUnique = () => {
      return () => ({
        uniq: [] as any[],
        push: function (record: any) {
          const value = record[DETECTION_ID];
          if (!this.uniq.includes(value)) {
            this.uniq.push(value);
          }
        },
        value: function () { return this.uniq.length; },
        format: (x: any) => x,
      });
    };

    const {
            'Count Unique Values': _y,
            'List Unique Values': _,
            'Integer Sum': _b,
            First,
            Last,
            '80% Upper Bound': _c,
            '80% Lower Bound': _d,
            'Sum over Sum': _e,
            // 'Sample Variance': _x,
            // 'Sample Standard Deviation': _w,
            'Sum as Fraction of Total': _f,
            'Sum as Fraction of Rows': _g,
            'Sum as Fraction of Columns': _h,
            'Count as Fraction of Total': _i,
            'Count as Fraction of Rows': _j,
            'Count as Fraction of Columns': _k,
            ...remainingAggregators
} = baseAggregators;

    return {
      // ...baseAggregators,
      ...remainingAggregators,
      'Average Detection Time': averageDetectionTime,
      'Average Region Time': averageRegionTime,
      'Count of Detections': countUnique
    };
  }, [corePivotUtilities]); // Recompute if corePivotUtilities changes

  // Memoize custom sorters
  const mySorters = useMemo(() => {
    if (!corePivotUtilities) return {};
    return {
      'Day of the week': corePivotUtilities.sortAs(DAYS_OF_WEEK),
      'Day Part': corePivotUtilities.sortAs(DAY_PART),
    };
  }, [corePivotUtilities]);

  // Callback for pivot table state changes
  const handlePivotTableChange = useCallback((s: any) => {
    setPivotState({
      ...s,
      aggregators: customAggregators,
      sorters: mySorters,
      // hiddenAttributes: [
      //   'detection_id', // List your hidden attributes here
      //   'detection_time_spent', // List your hidden attributes here
      //   'region_time_spent', // List your hidden attributes here
      // ],
 });
  }, [customAggregators, mySorters, downloadingPivot]);

  // Callback for selected artifact change
  const handleSelectedArtifactChange = useCallback((id: string) => {
    const matched = existingArtifacts.find((rt) => rt.uuid === id);
    setSelectedArtifactId(id);
    setSelectedArtifact(matched || DEFAULT_SELECTED_ARTIFACT);
  }, [existingArtifacts]);

  // Effect to load report artifact list on franchise change or initial load
  useEffect(() => {
    if (!selectedFranchise) {
      // Reset artifacts if no franchise is selected
      setExistingArtifacts([]);
      handleSelectedArtifactChange(''); // Clear selected artifact
      return;
    }

    getReportArtifactList(selectedFranchise.id)
      .then((response) => {
        setExistingArtifacts(
          Array.isArray(response)
            ? response.map(a => ({ uuid: a.uuid, size: a.size, lastModified: a.lastModified }))
            : []
        );
      })
      .catch((error) => {
        toast.error('Failed to load available reports.');
      });

    // Reset selected artifact and data when franchise changes
    setSelectedArtifact(DEFAULT_SELECTED_ARTIFACT);
    handleSelectedArtifactChange('');
    setReportData([]);
  }, [selectedFranchise]); // Depend on handleSelectedArtifactChange

  // Effect to load specific report data when selectedArtifactId changes
  useEffect(() => {
    setReportData([]);
    setPivotState({});
    if (!selectedArtifactId || !selectedFranchise) {
      return;
    }

    setDownloadingPivot(true); // Set loading state
    getReportArtifactById(selectedFranchise.id, selectedArtifactId)
      .then((response) => {
        if (response && typeof response === 'string') {
            // Use fetch with progress bar for large files
            const downloadUrl = response;
            setDownloadingPivot(true);
            fetch(downloadUrl)
            .then(async res => {
              if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
              // Track progress if possible
              const contentLength = res.headers.get('content-length');
              if (!contentLength) {
              // Fallback: no progress info
              return await res.json();
              }
              const total = parseInt(contentLength, 10);
              let loaded = 0;
              const reader = res.body?.getReader();
              const chunks: Uint8Array[] = [];
              while (reader) {
              const { done, value } = await reader.read();
              if (done) break;
              if (value) {
                chunks.push(value);
                loaded += value.length;
                setCurrentBytes(Number((loaded / (1024 * 1024)).toFixed(1))); // Convert to MB, 1 decimal
                setTotalBytes(Number((total / (1024 * 1024)).toFixed(1))); // Convert to MB, 1 decimal
                setDownloadingPivot(Math.round((loaded / total) * 100)); // Set as percent
              }
              }
              // Combine chunks and decode
              const blob = new Blob(chunks, { type: 'application/json' });
              const text = await blob.text();
              return JSON.parse(text);
            })
            .then(jsonData => {
              setReportData(Array.isArray(jsonData) ? jsonData : []);
              toast.success('Report data loaded successfully!');
              setDownloadingPivot(false); // Reset loading state
            })
            .catch(err => {
              console.error('Failed to load report data from downloadUrl:', err);
              toast.error('Failed to load report data.');
              setReportData([]);
              setDownloadingPivot(false); // Reset loading state
            });
        } else {
          // If response is an object, try to find a downloadUrl property
          const downloadUrl = (response)?.downloadUrl;
          if (downloadUrl && typeof downloadUrl === 'string') {
            fetch(downloadUrl)
              .then(async res => {
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                return await res.json();
              })
              .then(jsonData => {
                setReportData(Array.isArray(jsonData) ? jsonData : []);
                toast.success('Report data loaded successfully!');
              })
              .catch(err => {
                console.error('Failed to load report data from downloadUrl:', err);
                toast.error('Failed to load report data.');
                setReportData([]);
              });
          } else {
            console.error('No valid downloadUrl found in report artifact response:', response);
            toast.error('No download URL found for report data.');
            setReportData([]);
          }
        }
      })
      .catch((error) => {
        console.error('Failed to load report artifact details:', error);
        toast.error('Failed to retrieve report details.');
        setReportData([]);
      });
  }, [selectedArtifactId]);

  useEffect(() => {
  handlePivotTableChange({});
}, []);

  return (
    <>
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 space-y-6 border border-slate-200 dark:border-slate-700">
          <Toaster position="bottom-right" /> {/* Adjusted position for better visibility */}
          <div data-cy="analytics" className="space-y-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">
              Report Viewer
            </h1>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4"> {/* Added flex for better layout */}
              <label htmlFor="report-dropdown" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Select Report (MAX 200 MB):
              </label>
                <div className="flex-grow w-full md:w-auto"> {/* Allow dropdown to grow */}
                <DropdownFull
                  options={existingArtifacts.map(
                  (artifact: ReportArtifactMetadata) => ({
                    id: artifact.uuid,
                    value: artifact.uuid + ` (${(artifact.size / (1024 * 1024)).toFixed(2)} MB)`,
                    disabled: artifact.size > 200 * 1024 * 1024,
                  })
                  )}
                  selected={{
                  id: selectedArtifact.uuid,
                  value: selectedArtifact.lastModified,
                  }}
                  handleChange={handleSelectedArtifactChange}
                  dataCy="dropdown-existing-config"
                  id="report-dropdown"
                />
                </div>
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6 space-y-4">
              {reportData.length > 0 ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-2 shadow-inner" style={{ maxHeight: '600px', overflow: 'auto' }}>
                  <PivotTableUI
                    data={reportData}
                    onChange={handlePivotTableChange}
                    {...pivotState}
                    renderers={ALL_RENDERERS}
                    aggregators={customAggregators}
                    sorters={mySorters}

                  />
                </div>
              ) : (
                <div className="text-center text-slate-500 dark:text-slate-400 p-8">
                  {selectedArtifactId ? (
                      downloadingPivot ? (
                        <div className="flex flex-col items-center gap-2 mt-4">
                          <LoadingSpinner className="mx-auto" />
                          <div className="w-full max-w-xs">
                          <div className="h-2 bg-slate-200 dark:bg-slate-600 rounded">

                            <div
                            className="h-2 bg-blue-500 rounded transition-all duration-200"
                            style={{ width: `${typeof downloadingPivot === 'number' ? downloadingPivot : 0}%` }}
                            />
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-center">
                            Loading... {typeof downloadingPivot === 'number' ? downloadingPivot : 0}%
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-center">
                            {currentBytes}MB  / {totalBytes}MB
                          </div>
                          </div>
                        </div>
                      ) : (
                        <p>No data available for this report.</p>
                      )
                  ) : (
                    <p>Please select a report from the dropdown above to view its data.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
    </>
  );
}
